// src/components/family/profile/RecentActivityCard.tsx
// Child-specific recent activity feed

import React from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  ChevronRight,
  Calendar,
  FileText,
  Target,
  Pill,
  Heart,
  Clock,
  CheckCircle2,
  AlertCircle,
  type LucideIcon,
} from 'lucide-react';
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';
import type { ProfileActivity } from '@/features/children/hooks/useChildProfile';

interface RecentActivityCardProps {
  activities: ProfileActivity[];
  childId: string;
  isLoading?: boolean;
  className?: string;
}

// Activity type configuration
const activityConfig: Record<
  string,
  { icon: LucideIcon; color: string; bgColor: string }
> = {
  health_log: {
    icon: Activity,
    color: 'text-sage',
    bgColor: 'bg-sage/10',
  },
  appointment: {
    icon: Calendar,
    color: 'text-terracotta',
    bgColor: 'bg-terracotta/10',
  },
  document: {
    icon: FileText,
    color: 'text-navy',
    bgColor: 'bg-navy/10',
  },
  goal: {
    icon: Target,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50',
  },
  medication: {
    icon: Pill,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
  },
};

// Format timestamp with smart relative time
const formatActivityTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  
  if (isToday(date)) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  
  if (isYesterday(date)) {
    return `Yesterday at ${format(date, 'h:mm a')}`;
  }
  
  // Within the last week, show day name
  const daysAgo = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (daysAgo < 7) {
    return format(date, "EEEE 'at' h:mm a");
  }
  
  // Older than a week, show full date
  return format(date, 'MMM d, yyyy');
};

const ActivityItem: React.FC<{ activity: ProfileActivity }> = ({ activity }) => {
  const config = activityConfig[activity.type] || {
    icon: Activity,
    color: 'text-warm-gray',
    bgColor: 'bg-cream-dark',
  };
  const Icon = config.icon;

  return (
    <div className="flex gap-3 py-3 hover:bg-cream/50 px-4 -mx-4 transition-colors cursor-pointer group">
      {/* Icon */}
      <div
        className={`w-9 h-9 rounded-lg ${config.bgColor} flex items-center justify-center flex-shrink-0`}
      >
        <Icon className={`w-4.5 h-4.5 ${config.color}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-navy truncate group-hover:text-sage-dark transition-colors">
          {activity.title}
        </p>
        {activity.description && (
          <p className="text-xs text-warm-gray truncate mt-0.5">
            {activity.description}
          </p>
        )}
        <p className="text-xs text-warm-gray/70 mt-1 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatActivityTime(activity.timestamp)}
        </p>
      </div>

      {/* Arrow */}
      <ChevronRight className="w-4 h-4 text-warm-gray/50 opacity-0 group-hover:opacity-100 flex-shrink-0 self-center transition-opacity" />
    </div>
  );
};

export const RecentActivityCard: React.FC<RecentActivityCardProps> = ({
  activities,
  childId,
  isLoading = false,
  className = '',
}) => {
  if (isLoading) {
    return <RecentActivityCardSkeleton />;
  }

  return (
    <div className={`bg-white rounded-2xl border border-cream-dark ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-cream-dark">
        <h3 className="text-lg font-display font-semibold text-navy flex items-center gap-2">
          <Activity className="w-5 h-5 text-sage" />
          Recent Activity
        </h3>
        <Link
          to={`/family/children/${childId}/activity`}
          className="text-sm text-sage font-medium hover:text-sage-dark flex items-center gap-1 transition-colors"
        >
          View All
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Activity list */}
      <div className="px-4 py-2 divide-y divide-cream-dark/50">
        {activities.length > 0 ? (
          activities.slice(0, 5).map((activity) => (
            <ActivityItem key={activity.id} activity={activity} />
          ))
        ) : (
          <EmptyActivityState childId={childId} />
        )}
      </div>

      {/* Quick action footer */}
      {activities.length > 0 && (
        <div className="px-4 py-3 border-t border-cream-dark bg-cream/30">
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/family/children/${childId}/health`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sage text-white rounded-lg text-xs font-medium hover:bg-sage-dark transition-colors"
            >
              <Activity className="w-3.5 h-3.5" />
              Log Health Entry
            </Link>
            <Link
              to={`/family/children/${childId}/documents`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-cream-dark rounded-lg text-xs font-medium text-navy hover:border-sage/30 transition-colors"
            >
              <FileText className="w-3.5 h-3.5" />
              Upload Document
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

// Empty state component
const EmptyActivityState: React.FC<{ childId: string }> = ({ childId }) => (
  <div className="py-8 text-center">
    <div className="w-14 h-14 rounded-full bg-cream-dark flex items-center justify-center mx-auto mb-3">
      <Activity className="w-7 h-7 text-warm-gray" />
    </div>
    <p className="font-medium text-navy mb-1">No activity yet</p>
    <p className="text-sm text-warm-gray mb-4">
      Start tracking health logs, appointments, and more
    </p>
    <Link
      to={`/family/children/${childId}/health`}
      className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg font-medium text-sm hover:bg-sage-dark transition-colors"
    >
      <Activity className="w-4 h-4" />
      Add First Entry
    </Link>
  </div>
);

// Loading skeleton
export const RecentActivityCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-2xl border border-cream-dark animate-pulse">
    <div className="flex items-center justify-between px-4 py-3 border-b border-cream-dark">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-cream-dark rounded" />
        <div className="h-6 bg-cream-dark rounded w-32" />
      </div>
      <div className="h-4 bg-cream-dark rounded w-16" />
    </div>

    <div className="px-4 py-2 space-y-1">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex gap-3 py-3">
          <div className="w-9 h-9 rounded-lg bg-cream-dark" />
          <div className="flex-1 space-y-1.5">
            <div className="h-4 bg-cream-dark rounded w-3/4" />
            <div className="h-3 bg-cream-dark rounded w-1/2" />
            <div className="h-3 bg-cream-dark rounded w-24" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default RecentActivityCard;
